import { Drawer } from 'antd';
import appLogoDark from 'assets/logos/va-logo.png';
import clsx from 'clsx';
import { sidebarCollapsedStateSelector } from 'features/app-ui/selectors';
import { changeSidebarCollapsedState } from 'features/app-ui/slice';
import { useCrossTabAuthSync } from 'features/auth/hooks/useCrossTabAuthSync';
import { useProactiveTokenRefresh } from 'features/auth/hooks/useProactiveTokenRefresh';
import { useAutoPushSubscribe } from 'features/push-notification/useAutoPushSubscribe';
import { useAppDispatch, useAppSelector } from 'lib/stores/hook';
import { Suspense, useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { getPath } from 'routes/router-paths';
import { AppMainNavigationSideBar } from 'shared/components/AppMainNavigationSideBar';
import { AppMainNavigationTabBar } from 'shared/components/AppMainNavigationTabBar';
import { ScrollRestoration } from 'shared/components/ScrollRestoration';
import { SidebarCollapseToggle } from 'shared/components/SidebarCollapseToggle';
import { SplashScreen } from 'shared/components/SplashScreen';

const MOBILE_BREAKPOINT = 1024;

export const MainLayout = () => {
  useAutoPushSubscribe();
  useCrossTabAuthSync();
  useProactiveTokenRefresh();

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const isSidebarCollapsed = useAppSelector(sidebarCollapsedStateSelector);

  const [isMobile, setIsMobile] = useState(
    () =>
      typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT
  );
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const handleCollapseChange = useCallback(
    (next: boolean) => {
      dispatch(changeSidebarCollapsedState(next));
    },
    [dispatch]
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    };
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobile) {
      setIsMobileDrawerOpen(false);
    }
  }, [location.key, isMobile]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    // Check initial scroll position
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const sidebarContent = (
    <div className="flex h-full flex-col bg-primary dark:bg-[--bg-dark]">
      <figure
        className="block w-full shrink-0 cursor-pointer pb-3 pt-5.5 text-center"
        onClick={() => {
          navigate(getPath('dashboardPage'));
          if (isMobile) {
            setIsMobileDrawerOpen(false);
          }
        }}
      >
        <img
          src={appLogoDark}
          alt="Viet An Travel"
          width={50}
          className="mx-auto"
        />
      </figure>

      <nav
        role="navigation"
        className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden pb-4"
      >
        <AppMainNavigationSideBar />
      </nav>

      {!isMobile && (
        <div className="shrink-0 border-t border-white/10 p-3">
          <SidebarCollapseToggle
            isCollapsed={isSidebarCollapsed}
            onChange={handleCollapseChange}
          />
        </div>
      )}
    </div>
  );

  return (
    <div className="flex h-screen bg-primary" role="main">
      {!isMobile && (
        <aside
          className={clsx(
            'flex h-full shrink-0 flex-col overflow-hidden bg-primary transition-all dark:bg-[--bg-dark]',
            isSidebarCollapsed ? 'w-0' : 'w-50'
          )}
        >
          {sidebarContent}
        </aside>
      )}

      {isMobile && (
        <Drawer
          placement="left"
          open={isMobileDrawerOpen}
          onClose={() => setIsMobileDrawerOpen(false)}
          styles={{ body: { padding: 0 } }}
          closable={false}
          width={250}
        >
          {sidebarContent}
        </Drawer>
      )}

      <div
        className="w-full flex-1 overflow-x-hidden bg-white min-1025:rounded-ss-2xl"
        onScroll={e =>
          setIsScrolled((e.currentTarget as HTMLDivElement).scrollTop > 0)
        }
      >
        <header
          className={clsx(
            'sticky top-0 z-20 bg-white px-12 transition-shadow duration-300 max-1024:px-6',
            isScrolled && 'shadow-md'
          )}
        >
          <AppMainNavigationTabBar
            isSidebarCollapsed={isMobile ? true : isSidebarCollapsed}
            onSidebarExpand={
              isMobile
                ? () => setIsMobileDrawerOpen(true)
                : () => handleCollapseChange(false)
            }
          />
        </header>

        <main className="mx-auto min-h-[calc(100dvh-76px-48px)] max-w-[1920px] px-12 py-4 max-1024:px-6">
          <Suspense
            fallback={
              <ScrollRestoration>
                <SplashScreen fullScreen />
              </ScrollRestoration>
            }
          >
            <Outlet />
          </Suspense>
        </main>

        <footer
          className={clsx(
            'flex items-center justify-between gap-4 px-12 py-4 text-xs font-normal leading-[16px] text-[#858585]',
            'max-1024:px-6'
          )}
        >
          <span>Copyright &copy; 2025</span>
          <div className="flex items-center justify-end gap-2 max-1024:justify-start">
            <span>Hướng dẫn</span>
            <div className="h-1 w-1 rounded-full bg-[#858585]" />
            <span>Hỗ trợ</span>
          </div>
        </footer>
      </div>
    </div>
  );
};
